import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import {
  Alerta,
  Botao,
  CabecalhoPagina,
  Carregando,
  ConteudoPagina,
  EstadoErro,
  EstadoVazio,
  SeloStatus,
  Tabela,
} from '../../components/ui';
import { mensagemDeErro } from '../../lib/erros';
import { useSessao } from '../auth/contexto';
import { useBuscaGestante, useEmAcompanhamento, type ResultadoBusca } from './api';
import { BuscaGestante } from './BuscaGestante';
import { dataBR, idadeEmAnos, igHoje, seloGestacao } from './formatos';

export const linkBotao =
  'inline-flex h-11 items-center gap-2 rounded-lg bg-primaria px-5 text-sm font-medium text-primaria-texto hover:bg-primaria-hover';

/** Nome social em destaque, com o nome civil abaixo (respeito à identidade). */
export function NomeGestante({ nome, nomeSocial }: { nome: string; nomeSocial: string | null }) {
  if (!nomeSocial) return <>{nome}</>;
  return (
    <span className="flex flex-col">
      <span>{nomeSocial}</span>
      <span className="text-xs font-normal text-texto-suave">{`Nome civil: ${nome}`}</span>
    </span>
  );
}

export function PaginaPacientes() {
  const { pode } = useSessao();
  // A ficha devolve o termo ao voltar (estado do histórico: não fica na URL nem no servidor)
  const { state } = useLocation() as { state: { termo?: string } | null };
  const [termo, setTermo] = useState<string | null>(state?.termo ?? null);
  const busca = useBuscaGestante(termo);
  const veGestacao = pode('gestacoes:ver');
  const [pagina, setPagina] = useState(1);
  // Sem busca, médico, enfermeiro e gestor veem as gestantes em acompanhamento (a recepção, não)
  const lista = useEmAcompanhamento(pagina, veGestacao && termo === null);
  const podeCadastrar = pode('pacientes:cadastrar');
  const veSeguimento = pode('puericultura:ver');

  const colunas = [
    {
      titulo: 'Nome',
      celula: (p: ResultadoBusca) =>
        p.tipo_paciente === 'crianca' ? (
          // Criança em seguimento (D-040): abre a ficha do seguimento
          <span className="flex flex-col gap-1">
            {veSeguimento ? (
              <Link to={`/criancas/${p.id}`} className="font-medium text-primaria hover:underline">
                {p.nome}
              </Link>
            ) : (
              <span className="font-medium">{p.nome}</span>
            )}
            <span className="text-xs text-texto-suave">
              {`Criança${p.nome_mae ? ` · mãe: ${p.nome_mae}` : ''}`}
            </span>
          </span>
        ) : (
          <Link
            to={`/pacientes/${p.id}`}
            state={{ termo }}
            className="font-medium text-primaria hover:underline"
          >
            <NomeGestante nome={p.nome} nomeSocial={p.nome_social} />
          </Link>
        ),
    },
    {
      titulo: 'Nascimento',
      celula: (p: ResultadoBusca) =>
        p.tipo_paciente === 'crianca'
          ? dataBR(p.data_nascimento)
          : `${dataBR(p.data_nascimento)} (${idadeEmAnos(p.data_nascimento)} anos)`,
    },
    { titulo: 'CPF', celula: (p: ResultadoBusca) => p.cpf_mascarado ?? '—', secundaria: true },
    { titulo: 'Unidade', celula: (p: ResultadoBusca) => p.unidade_nome ?? '—', secundaria: true },
    ...(veGestacao
      ? [
          {
            titulo: 'Gestação',
            celula: (p: ResultadoBusca) => {
              if (p.tipo_paciente === 'crianca') return '—';
              if (!p.gestacao)
                return <SeloStatus tipo="neutro">Sem gestação em andamento</SeloStatus>;
              const ig = igHoje(p.gestacao.data_ultima_menstruacao);
              const selo = seloGestacao(p.gestacao.status);
              return (
                <span className="flex flex-col gap-1">
                  <SeloStatus tipo={selo.tipo}>{selo.texto}</SeloStatus>
                  <span className="text-xs text-texto-suave">
                    {ig ? `IG ${ig} · ` : ''}
                    {`DPP ${dataBR(p.gestacao.data_provavel_parto)}`}
                  </span>
                </span>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <ConteudoPagina>
      <CabecalhoPagina
        ajuda="cadastro#buscar"
        titulo="Gestantes e parceiros"
        descricao="Busque a gestante antes de cadastrar, para não criar um cadastro em duplicidade."
        acoes={
          podeCadastrar ? (
            <Link to="/pacientes/nova" className={linkBotao}>
              <Plus className="size-4" aria-hidden="true" />
              Cadastrar gestante
            </Link>
          ) : undefined
        }
      />
      <BuscaGestante inicial={termo ?? ''} aoBuscar={setTermo} buscando={busca.isFetching} />

      {termo !== null && veGestacao && (
        <div>
          <Botao variante="fantasma" tamanho="pequeno" onClick={() => setTermo(null)}>
            Ver as gestantes em acompanhamento
          </Botao>
        </div>
      )}

      <section aria-live="polite" aria-label="Resultado da busca" className="flex flex-col gap-3">
        {termo === null ? null : busca.isPending ? (
          <Carregando linhas={4} rotulo="Buscando…" />
        ) : busca.isError ? (
          <EstadoErro
            descricao={mensagemDeErro(busca.error)}
            aoTentarNovamente={() => void busca.refetch()}
          />
        ) : busca.data.itens.length === 0 ? (
          <EstadoVazio
            titulo="Nenhuma gestante encontrada."
            descricao={
              podeCadastrar
                ? 'Confira o que foi digitado. Se ela ainda não tem cadastro, cadastre agora.'
                : 'Confira o que foi digitado.'
            }
            acao={
              podeCadastrar ? (
                <Link to="/pacientes/nova" className={linkBotao}>
                  <Plus className="size-4" aria-hidden="true" />
                  Cadastrar gestante
                </Link>
              ) : undefined
            }
          />
        ) : (
          <>
            <p className="text-sm text-texto-suave">
              {busca.data.itens.length === 1
                ? '1 gestante encontrada.'
                : `${busca.data.itens.length} gestantes encontradas.`}
            </p>
            {busca.data.mais && (
              <Alerta
                tipo="info"
                titulo="Há mais resultados. Refine a busca (por exemplo, digite o nome completo)."
              />
            )}
            <Tabela
              legenda="Gestantes encontradas"
              itens={busca.data.itens}
              chave={(p) => p.id}
              colunas={colunas}
            />
          </>
        )}
      </section>

      {termo === null && veGestacao && (
        <section aria-labelledby="titulo-acompanhamento" className="flex flex-col gap-3">
          <h2 id="titulo-acompanhamento" className="text-lg font-semibold text-texto">
            Gestantes em acompanhamento
          </h2>
          {lista.isPending ? (
            <Carregando linhas={4} />
          ) : lista.isError ? (
            <EstadoErro
              descricao={mensagemDeErro(lista.error)}
              aoTentarNovamente={() => void lista.refetch()}
            />
          ) : lista.data.precisaMunicipio ? (
            <EstadoVazio
              titulo="Seu usuário não está vinculado a um município."
              descricao="Use a busca acima ou peça ao administrador para vincular o seu usuário."
            />
          ) : lista.data.total === 0 ? (
            <EstadoVazio titulo="Nenhuma gestante com gestação em andamento no seu município." />
          ) : (
            <>
              <p className="text-sm text-texto-suave">
                {`${lista.data.total} ${lista.data.total === 1 ? 'gestante' : 'gestantes'} com gestação em andamento, da data provável do parto mais próxima à mais distante.`}
              </p>
              <Tabela
                legenda="Gestantes em acompanhamento"
                itens={lista.data.itens}
                chave={(p) => p.id}
                colunas={colunas}
              />
              {lista.data.total > lista.data.porPagina && (
                <nav aria-label="Páginas" className="flex flex-wrap items-center gap-3">
                  <Botao
                    variante="contorno"
                    tamanho="pequeno"
                    disabled={pagina === 1}
                    onClick={() => setPagina((p) => p - 1)}
                  >
                    Anterior
                  </Botao>
                  <span className="text-sm text-texto-suave">
                    {`Página ${pagina} de ${Math.ceil(lista.data.total / lista.data.porPagina)}`}
                  </span>
                  <Botao
                    variante="contorno"
                    tamanho="pequeno"
                    disabled={pagina * lista.data.porPagina >= lista.data.total}
                    onClick={() => setPagina((p) => p + 1)}
                  >
                    Próxima
                  </Botao>
                </nav>
              )}
            </>
          )}
        </section>
      )}
    </ConteudoPagina>
  );
}
